import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { useCreateFiscalYear, useFiscalYears } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'

interface FiscalYearCreateModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
}

function addOneDay(dateStr: string): string {
  const date = new Date(dateStr)
  date.setDate(date.getDate() + 1)
  return date.toISOString().split('T')[0]!
}

export function FiscalYearCreateModal({
  isOpen,
  onClose,
  residenceId,
}: FiscalYearCreateModalProps) {
  const { data: fiscalYears } = useFiscalYears(residenceId)

  const getDefaultDates = (): { name: string; start_date: string; end_date: string } => {
    const currentYear = new Date().getFullYear()

    // Si des exercices existent, proposer le suivant
    if (fiscalYears && fiscalYears.length > 0) {
      // Trier par date de fin décroissante pour avoir le plus récent
      const sortedYears = [...fiscalYears].sort(
        (a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime()
      )
      const lastYear = sortedYears[0]!

      // Calculer la durée de l'exercice précédent
      const lastStart = new Date(lastYear.start_date!)
      const lastEnd = new Date(lastYear.end_date!)
      const durationMs = lastEnd.getTime() - lastStart.getTime()

      // Nouveau début = jour après la fin du dernier exercice
      const newStart = addOneDay(lastYear.end_date!)
      const newStartDate = new Date(newStart)
      const newEndDate = new Date(newStartDate.getTime() + durationMs)
      const newEnd = newEndDate.toISOString().split('T')[0]!

      // Extraire l'année pour le nom
      const startYear = newStartDate.getFullYear()
      const endYear = newEndDate.getFullYear()
      const name = startYear === endYear
        ? `Exercice ${startYear}`
        : `Exercice ${startYear}-${endYear}`

      return { name, start_date: newStart, end_date: newEnd }
    }

    // Sinon, proposer l'année en cours
    return {
      name: `Exercice ${currentYear}`,
      start_date: `${currentYear}-01-01`,
      end_date: `${currentYear}-12-31`,
    }
  }

  const [formData, setFormData] = useState(getDefaultDates())

  // Mettre à jour les valeurs par défaut quand les exercices sont chargés
  useEffect(() => {
    if (isOpen) {
      setFormData(getDefaultDates())
    }
  }, [isOpen, fiscalYears])

  const createFiscalYear = useCreateFiscalYear()

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toaster.create({ title: 'Erreur', description: 'Veuillez remplir tous les champs', type: 'error' })
      return
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toaster.create({ title: 'Erreur', description: 'La date de fin doit être postérieure à la date de début', type: 'error' })
      return
    }

    try {
      await createFiscalYear.mutateAsync({
        residence_id: residenceId,
        name: formData.name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: 'draft',
      })
      toaster.create({ title: 'Succès', description: 'Exercice comptable créé avec succès', type: 'success' })
      handleClose()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la création'
      toaster.create({ title: 'Erreur', description: message, type: 'error' })
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="md">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Créer un exercice comptable</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Flex direction="column" gap={4}>
              <Field label="Nom de l'exercice" required>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Exercice 2025"
                />
              </Field>

              <Flex gap={4}>
                <Box flex={1}>
                  <Field label="Date de début" required>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </Field>
                </Box>
                <Box flex={1}>
                  <Field label="Date de fin" required>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </Field>
                </Box>
              </Flex>

              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  L'exercice comptable définit la période sur laquelle seront enregistrées
                  les opérations comptables, les budgets et les appels de fonds.
                </Text>
              </Box>
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createFiscalYear.isPending}
            >
              Créer l'exercice
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
